import React, { useState } from 'react';

/*
 * Clean line-art anatomical body map — front + back views.
 * Style: thin outlines on dark background, muscles fill with color when highlighted.
 * 
 * Props:
 *   muscleData: { [muscleKey]: { color, recovery_pct, source_exercises, hours_since_trained } }
 *   highlightMode: 'recovery' | 'exercise'
 *   primaryMuscles: string[]
 *   secondaryMuscles: string[]
 *   size: 'sm' | 'md' | 'lg'
 *   showLabels: boolean
 *   interactive: boolean
 *   showBack: boolean
 *   className: string
 */

// ── Visual Constants ──────────────────────────────────────────
const THEME = {
  bodyStroke: '#8B8B96',
  bodyFill: 'none',
  muscleStroke: '#6B6B75',
  muscleFillDefault: 'rgba(255,255,255,0.03)',
  primary: '#CCFF00',
  primaryFill: 'rgba(204,255,0,0.50)',
  primaryStroke: '#CCFF00',
  secondary: '#F59E0B',
  secondaryFill: 'rgba(245,158,11,0.40)',
  secondaryStroke: '#F59E0B',
  detailStroke: '#4A4A55',
  labelColor: '#9CA3AF',
};

const getRecoveryStyle = (pct) => {
  if (pct >= 85) return { fill: 'rgba(34,197,94,0.45)', stroke: '#22C55E' };
  if (pct >= 70) return { fill: 'rgba(74,222,128,0.38)', stroke: '#4ADE80' };
  if (pct >= 55) return { fill: 'rgba(253,224,71,0.35)', stroke: '#FDE047' };
  if (pct >= 40) return { fill: 'rgba(251,146,60,0.40)', stroke: '#FB923C' };
  if (pct >= 25) return { fill: 'rgba(248,113,113,0.42)', stroke: '#F87171' };
  return { fill: 'rgba(239,68,68,0.48)', stroke: '#EF4444' };
};

// ── FRONT BODY OUTLINE ────────────────────────────────────────
// Clean anatomical outline paths — rendered as stroke only
const FRONT_BODY = [
  // Head
  'M100,8 C115,8 125,18 125,32 C125,44 115,53 100,53 C85,53 75,44 75,32 C75,18 85,8 100,8 Z',
  // Neck
  'M91,51 L109,51 L107,64 L93,64 Z',
  // Torso
  'M56,70 C59,66 78,62 100,62 C122,62 141,66 144,70 L147,77 C144,75 141,74 137,74 L137,197 C135,203 131,211 125,217 L75,217 C69,211 65,203 63,197 L63,74 C59,74 56,75 53,77 Z',
  // Left upper arm
  'M53,77 C48,81 43,89 39,99 L35,127 C33,139 32,149 32,159 L47,159 C47,151 47,141 49,129 L52,101 C54,93 56,85 59,79 Z',
  // Left forearm
  'M32,159 L47,159 L44,191 L41,219 L39,237 L25,237 L28,219 L31,191 Z',
  // Left hand
  'M25,237 L39,237 L37,252 C36,256 34,260 30,260 L23,260 C21,256 22,250 24,244 Z',
  // Right upper arm
  'M147,77 C152,81 157,89 161,99 L165,127 C167,139 168,149 168,159 L153,159 C153,151 153,141 151,129 L148,101 C146,93 144,85 141,79 Z',
  // Right forearm
  'M168,159 L153,159 L156,191 L159,219 L161,237 L175,237 L172,219 L169,191 Z',
  // Right hand
  'M175,237 L161,237 L163,252 C164,256 166,260 170,260 L177,260 C179,256 178,250 176,244 Z',
  // Left upper leg
  'M63,217 L97,217 C98,223 99,231 99,237 L97,276 L95,303 C94,311 92,317 91,321 L69,321 C68,317 66,311 65,303 L64,276 C63,257 63,237 64,225 Z',
  // Left lower leg
  'M69,321 L91,321 C91,329 90,337 89,347 L87,373 L85,397 C84,405 83,411 81,415 L75,415 C74,411 73,405 73,397 L71,373 C70,359 69,347 69,335 Z',
  // Left foot
  'M75,415 L81,415 L82,423 C83,427 81,432 77,433 L71,433 C68,431 67,427 68,423 Z',
  // Right upper leg
  'M137,217 L103,217 C102,223 101,231 101,237 L103,276 L105,303 C106,311 108,317 109,321 L131,321 C132,317 134,311 135,303 L136,276 C137,257 137,237 136,225 Z',
  // Right lower leg
  'M131,321 L109,321 C109,329 110,337 111,347 L113,373 L115,397 C116,405 117,411 119,415 L125,415 C126,411 127,405 127,397 L129,373 C130,359 131,347 131,335 Z',
  // Right foot
  'M125,415 L119,415 L118,423 C117,427 119,432 123,433 L129,433 C132,431 133,427 132,423 Z',
];

// ── FRONT MUSCLES ──────────────────────────────────────────────
const FRONT_MUSCLES = {
  shoulders: [
    // Left anterior deltoid
    'M75,69 C69,67 59,67 51,73 C45,79 41,87 39,95 C38,99 39,103 43,105 L49,103 C51,97 55,89 61,81 C67,75 73,71 75,70 Z',
    // Left lateral deltoid
    'M71,70 C67,73 61,79 57,85 C53,91 51,97 53,101 L59,99 C61,95 65,89 69,83 C71,79 71,75 71,70 Z',
    // Right anterior deltoid
    'M125,69 C131,67 141,67 149,73 C155,79 159,87 161,95 C162,99 161,103 157,105 L151,103 C149,97 145,89 139,81 C133,75 127,71 125,70 Z',
    // Right lateral deltoid
    'M129,70 C133,73 139,79 143,85 C147,91 149,97 147,101 L141,99 C139,95 135,89 131,83 C129,79 129,75 129,70 Z',
  ],
  chest: [
    // Left pec upper
    'M99,73 C93,70 83,69 75,73 C69,77 65,85 65,93 L65,99 C67,93 71,85 79,79 C87,75 95,73 99,74 Z',
    // Left pec lower
    'M99,75 C91,79 81,85 73,93 C67,101 65,109 67,115 C69,119 75,123 85,123 C95,123 99,121 99,119 Z',
    // Right pec upper
    'M101,73 C107,70 117,69 125,73 C131,77 135,85 135,93 L135,99 C133,93 129,85 121,79 C113,75 105,73 101,74 Z',
    // Right pec lower
    'M101,75 C109,79 119,85 127,93 C133,101 135,109 133,115 C131,119 125,123 115,123 C105,123 101,121 101,119 Z',
  ],
  biceps: [
    // Left bicep
    'M55,103 C51,109 47,119 45,131 C43,141 43,151 45,156 C47,159 51,161 55,159 C57,157 59,153 59,147 C59,139 58,129 57,121 C56,113 56,107 55,103 Z',
    // Left bicep inner
    'M57,107 C55,113 53,121 52,131 C51,139 51,147 53,153 L57,151 C56,145 56,137 57,129 C57,121 57,113 57,107 Z',
    // Right bicep
    'M145,103 C149,109 153,119 155,131 C157,141 157,151 155,156 C153,159 149,161 145,159 C143,157 141,153 141,147 C141,139 142,129 143,121 C144,113 144,107 145,103 Z',
    // Right bicep inner
    'M143,107 C145,113 147,121 148,131 C149,139 149,147 147,153 L143,151 C144,145 144,137 143,129 C143,121 143,113 143,107 Z',
  ],
  triceps: [
    // Left tricep
    'M39,103 C36,113 33,125 32,139 C31,149 32,155 35,159 L43,159 C41,155 41,147 42,137 C43,127 45,115 49,105 Z',
    // Right tricep
    'M161,103 C164,113 167,125 168,139 C169,149 168,155 165,159 L157,159 C159,155 159,147 158,137 C157,127 155,115 151,105 Z',
  ],
  core: [
    // Upper abs left
    'M87,125 C87,123 93,122 99,123 L99,141 C93,142 87,141 87,139 Z',
    // Upper abs right
    'M101,123 C107,122 113,123 113,125 L113,139 C113,141 107,142 101,141 Z',
    // Mid abs left
    'M85,146 C85,144 91,143 99,144 L99,165 C91,166 85,165 85,163 Z',
    // Mid abs right
    'M101,144 C109,143 115,144 115,146 L115,163 C115,165 109,166 101,165 Z',
    // Lower abs left
    'M83,170 C83,168 89,167 99,168 L99,193 C93,197 85,197 83,193 Z',
    // Lower abs right
    'M101,168 C111,167 117,168 117,170 L117,193 C115,197 107,197 101,193 Z',
    // Left oblique
    'M65,105 C64,115 64,127 65,141 L65,169 C67,179 69,187 73,197 L81,197 C77,187 75,179 73,169 L73,141 C73,129 73,117 75,107 Z',
    // Right oblique
    'M135,105 C136,115 136,127 135,141 L135,169 C133,179 131,187 127,197 L119,197 C123,187 125,179 127,169 L127,141 C127,129 127,117 125,107 Z',
    // Left serratus
    'M65,93 L75,93 L73,101 L65,101 Z M65,103 L75,103 L73,109 L65,109 Z M65,111 L75,111 L73,117 L65,117 Z',
    // Right serratus
    'M135,93 L125,93 L127,101 L135,101 Z M135,103 L125,103 L127,109 L135,109 Z M135,111 L125,111 L127,117 L135,117 Z',
  ],
  quads: [
    // Left rectus femoris
    'M79,223 C81,219 87,217 93,219 L93,293 C92,299 91,305 89,311 L79,311 C77,305 77,299 77,293 L77,237 C77,229 79,225 79,223 Z',
    // Left vastus lateralis
    'M65,225 C67,221 71,219 77,219 L77,293 C77,303 75,309 73,315 L65,319 C64,315 63,307 63,299 L63,273 C63,257 63,243 64,233 Z',
    // Left vastus medialis
    'M93,269 C94,277 94,289 93,299 C92,305 91,311 89,317 L83,319 C84,313 85,307 86,299 C87,291 89,281 91,271 Z',
    // Right rectus femoris
    'M121,223 C119,219 113,217 107,219 L107,293 C108,299 109,305 111,311 L121,311 C123,305 123,299 123,293 L123,237 C123,229 121,225 121,223 Z',
    // Right vastus lateralis
    'M135,225 C133,221 129,219 123,219 L123,293 C123,303 125,309 127,315 L135,319 C136,315 137,307 137,299 L137,273 C137,257 137,243 136,233 Z',
    // Right vastus medialis
    'M107,269 C106,277 106,289 107,299 C108,305 109,311 111,317 L117,319 C116,313 115,307 114,299 C113,291 111,281 109,271 Z',
  ],
  calves: [
    // Left calf medial
    'M71,327 C73,323 79,321 85,323 C87,325 88,331 87,339 L85,361 C84,371 82,381 80,391 L75,395 C73,389 71,379 70,367 L69,349 C69,339 70,333 71,327 Z',
    // Left calf lateral
    'M87,326 C89,324 91,324 92,328 L91,349 C90,361 89,371 87,379 L83,381 C85,373 86,363 87,351 Z',
    // Right calf medial
    'M129,327 C127,323 121,321 115,323 C113,325 112,331 113,339 L115,361 C116,371 118,381 120,391 L125,395 C127,389 129,379 130,367 L131,349 C131,339 130,333 129,327 Z',
    // Right calf lateral
    'M113,326 C111,324 109,324 108,328 L109,349 C110,361 111,371 113,379 L117,381 C115,373 114,363 113,351 Z',
  ],
};

// ── FRONT DETAIL LINES ─────────────────────────────────────────
const FRONT_DETAILS = [
  // Pec fibers
  { d: 'M97,77 C89,83 79,93 73,101', m: 'chest' },
  { d: 'M97,87 C89,93 81,103 77,111', m: 'chest' },
  { d: 'M97,97 C93,103 87,111 83,119', m: 'chest' },
  { d: 'M103,77 C111,83 121,93 127,101', m: 'chest' },
  { d: 'M103,87 C111,93 119,103 123,111', m: 'chest' },
  { d: 'M103,97 C107,103 113,111 117,119', m: 'chest' },
  // Linea alba
  { d: 'M100,123 L100,195', m: 'core' },
  // Ab inscriptions
  { d: 'M87,141 L113,141', m: 'core' },
  { d: 'M85,165 L115,165', m: 'core' },
  // Deltoid divisions
  { d: 'M57,73 C53,83 49,93 47,101', m: 'shoulders' },
  { d: 'M143,73 C147,83 151,93 153,101', m: 'shoulders' },
  // Quad divisions
  { d: 'M77,223 L77,315', m: 'quads' },
  { d: 'M93,219 L93,311', m: 'quads' },
  { d: 'M107,219 L107,311', m: 'quads' },
  { d: 'M123,223 L123,315', m: 'quads' },
  // Bicep peaks
  { d: 'M51,113 C49,125 47,137 49,149', m: 'biceps' },
  { d: 'M149,113 C151,125 153,137 151,149', m: 'biceps' },
];

// ── BACK MUSCLES ───────────────────────────────────────────────
const BACK_MUSCLES = {
  shoulders: [
    'M75,69 C69,67 59,67 51,73 C45,79 41,87 39,95 C38,99 39,103 43,105 L49,103 C51,97 55,89 61,81 C67,75 73,71 75,70 Z',
    'M71,70 C67,73 61,79 57,85 C53,91 51,97 53,101 L59,99 C61,95 65,89 69,83 C71,79 71,75 71,70 Z',
    'M125,69 C131,67 141,67 149,73 C155,79 159,87 161,95 C162,99 161,103 157,105 L151,103 C149,97 145,89 139,81 C133,75 127,71 125,70 Z',
    'M129,70 C133,73 139,79 143,85 C147,91 149,97 147,101 L141,99 C139,95 135,89 131,83 C129,79 129,75 129,70 Z',
  ],
  upper_back: [
    // Trapezius
    'M85,65 L100,61 L115,65 L119,73 C121,81 125,91 129,101 L125,107 L100,101 L75,107 L71,101 C75,91 79,81 81,73 Z',
    // Left rhomboid
    'M75,107 L99,101 L99,135 L79,141 C77,135 75,125 75,115 Z',
    // Right rhomboid
    'M125,107 L101,101 L101,135 L121,141 C123,135 125,125 125,115 Z',
    // Left lat
    'M63,101 L75,101 L75,121 C75,135 73,151 71,165 C69,175 67,183 65,189 L61,189 C61,181 61,167 61,153 L62,129 C62,115 63,107 63,101 Z',
    // Left lat lower
    'M61,153 C62,163 63,171 65,179 L71,179 C69,171 67,163 66,153 Z',
    // Right lat
    'M137,101 L125,101 L125,121 C125,135 127,151 129,165 C131,175 133,183 135,189 L139,189 C139,181 139,167 139,153 L138,129 C138,115 137,107 137,101 Z',
    // Right lat lower
    'M139,153 C138,163 137,171 135,179 L129,179 C131,171 133,163 134,153 Z',
  ],
  lower_back: [
    // Left erector
    'M87,143 L99,139 L99,195 C97,199 91,201 87,201 C85,199 85,193 85,187 L85,165 C85,155 86,149 87,143 Z',
    // Right erector
    'M113,143 L101,139 L101,195 C103,199 109,201 113,201 C115,199 115,193 115,187 L115,165 C115,155 114,149 113,143 Z',
  ],
  triceps: [
    // Left tricep horseshoe
    'M39,103 C36,113 33,127 31,141 C30,151 31,157 35,161 L55,161 C53,157 51,149 53,139 C55,127 57,115 59,105 Z',
    // Left tricep detail
    'M43,107 C41,115 39,127 37,139 C36,147 37,153 39,157 L47,157 C45,153 45,145 47,137 C49,127 51,115 52,109 Z',
    // Right tricep horseshoe
    'M161,103 C164,113 167,127 169,141 C170,151 169,157 165,161 L145,161 C147,157 149,149 147,139 C145,127 143,115 141,105 Z',
    // Right tricep detail
    'M157,107 C159,115 161,127 163,139 C164,147 163,153 161,157 L153,157 C155,153 155,145 153,137 C151,127 149,115 148,109 Z',
  ],
  glutes: [
    // Left glute max
    'M63,199 C65,193 73,189 85,191 C93,193 99,199 99,207 L99,223 C93,227 83,229 75,229 C69,227 65,221 63,215 Z',
    // Left glute med
    'M63,193 C65,187 73,183 83,185 L87,191 C77,189 67,193 65,199 Z',
    // Right glute max
    'M137,199 C135,193 127,189 115,191 C107,193 101,199 101,207 L101,223 C107,227 117,229 125,229 C131,227 135,221 137,215 Z',
    // Right glute med
    'M137,193 C135,187 127,183 117,185 L113,191 C123,189 133,193 135,199 Z',
  ],
  hamstrings: [
    // Left biceps femoris (outer)
    'M63,233 C65,227 69,225 75,227 L75,297 C75,305 73,311 71,315 L63,319 C63,315 62,307 62,299 L62,271 C62,257 62,243 63,233 Z',
    // Left semitendinosus (center)
    'M77,227 C79,223 85,221 89,223 L89,297 C89,303 87,309 85,315 L77,315 C77,309 77,303 77,297 Z',
    // Left semimembranosus (inner)
    'M91,223 C93,221 96,221 97,225 L96,291 C95,301 93,309 91,317 L87,317 C88,309 89,301 90,291 Z',
    // Right biceps femoris
    'M137,233 C135,227 131,225 125,227 L125,297 C125,305 127,311 129,315 L137,319 C137,315 138,307 138,299 L138,271 C138,257 138,243 137,233 Z',
    // Right semitendinosus
    'M123,227 C121,223 115,221 111,223 L111,297 C111,303 113,309 115,315 L123,315 C123,309 123,303 123,297 Z',
    // Right semimembranosus
    'M109,223 C107,221 104,221 103,225 L104,291 C105,301 107,309 109,317 L113,317 C112,309 111,301 110,291 Z',
  ],
  calves: [
    // Left gastrocnemius medial
    'M69,325 C71,321 77,319 83,321 L83,329 C83,339 81,353 79,363 L77,381 C76,389 74,395 72,399 L69,399 C69,393 68,383 68,373 L67,353 C67,341 68,333 69,325 Z',
    // Left gastrocnemius lateral
    'M85,321 C87,319 90,319 92,323 L91,343 C90,355 89,365 87,375 L85,383 L79,383 C81,375 83,363 84,351 L85,337 Z',
    // Right gastrocnemius medial
    'M131,325 C129,321 123,319 117,321 L117,329 C117,339 119,353 121,363 L123,381 C124,389 126,395 128,399 L131,399 C131,393 132,383 132,373 L133,353 C133,341 132,333 131,325 Z',
    // Right gastrocnemius lateral
    'M115,321 C113,319 110,319 108,323 L109,343 C110,355 111,365 113,375 L115,383 L121,383 C119,375 117,363 116,351 L115,337 Z',
  ],
};

// ── BACK DETAIL LINES ──────────────────────────────────────────
const BACK_DETAILS = [
  { d: 'M100,64 L87,77', m: 'upper_back' },
  { d: 'M100,64 L113,77', m: 'upper_back' },
  { d: 'M100,73 L79,95', m: 'upper_back' },
  { d: 'M100,73 L121,95', m: 'upper_back' },
  // Spine
  { d: 'M100,64 L100,197', m: 'lower_back' },
  // Erector spinae
  { d: 'M91,141 L91,193', m: 'lower_back' },
  { d: 'M109,141 L109,193', m: 'lower_back' },
  // Lat fibers
  { d: 'M73,105 L69,175', m: 'upper_back' },
  { d: 'M127,105 L131,175', m: 'upper_back' },
  // Hamstring seams
  { d: 'M74,225 L72,311', m: 'hamstrings' },
  { d: 'M86,223 L84,311', m: 'hamstrings' },
  { d: 'M114,223 L116,311', m: 'hamstrings' },
  { d: 'M126,225 L128,311', m: 'hamstrings' },
  // Glute crease
  { d: 'M65,213 C77,223 89,227 100,225 C111,227 123,223 135,213', m: 'glutes' },
  // Calf division
  { d: 'M81,325 L81,379', m: 'calves' },
  { d: 'M119,325 L119,379', m: 'calves' },
];

// ── MUSCLE LABELS ──────────────────────────────────────────────
const LABELS = {
  shoulders: 'Shoulders', chest: 'Chest', biceps: 'Biceps', triceps: 'Triceps',
  core: 'Core', upper_back: 'Upper Back', lower_back: 'Lower Back',
  glutes: 'Glutes', quads: 'Quads', hamstrings: 'Hamstrings', calves: 'Calves',
};

const formatMuscle = (key) =>
  LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// ── Single View Renderer ───────────────────────────────────────
const BodyView = ({
  bodyParts, muscles, details,
  muscleData, highlightMode, primaryMuscles, secondaryMuscles,
  hoveredMuscle, setHoveredMuscle, interactive, viewLabel, showDetails,
  overlay,
}) => {
  const getMuscleStyle = (key) => {
    const isHov = hoveredMuscle === key;

    if (highlightMode === 'exercise') {
      const isPrimary = primaryMuscles?.includes(key);
      const isSecondary = secondaryMuscles?.includes(key);
      if (isPrimary) return {
        fill: isHov ? THEME.primaryFill.replace('0.50', '0.65') : THEME.primaryFill,
        stroke: THEME.primaryStroke,
        strokeWidth: isHov ? 0.8 : 0.5,
        strokeOpacity: 0.8,
      };
      if (isSecondary) return {
        fill: isHov ? THEME.secondaryFill.replace('0.40', '0.55') : THEME.secondaryFill,
        stroke: THEME.secondaryStroke,
        strokeWidth: isHov ? 0.7 : 0.5,
        strokeOpacity: 0.7,
      };
      return {
        fill: THEME.muscleFillDefault,
        stroke: THEME.muscleStroke,
        strokeWidth: 0.4,
        strokeOpacity: 0.7,
      };
    }

    // Recovery mode
    const data = muscleData?.[key];
    if (!data || data.recovery_pct >= 100) {
      return {
        fill: THEME.muscleFillDefault,
        stroke: THEME.muscleStroke,
        strokeWidth: 0.4,
        strokeOpacity: 0.7,
      };
    }
    const rs = getRecoveryStyle(data.recovery_pct);
    return {
      fill: isHov ? rs.fill.replace(/[\d.]+\)$/, m => (parseFloat(m) + 0.15).toFixed(2) + ')') : rs.fill,
      stroke: rs.stroke,
      strokeWidth: isHov ? 0.8 : 0.5,
      strokeOpacity: isHov ? 0.9 : 0.7,
    };
  };

  const isActive = (key) => {
    if (highlightMode === 'exercise') {
      return primaryMuscles?.includes(key) || secondaryMuscles?.includes(key);
    }
    const data = muscleData?.[key];
    return data && data.recovery_pct < 100;
  };

  return (
    <svg viewBox="0 0 200 440" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <filter id={`glow-${viewLabel}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Body outline — thin strokes, no fill */}
      {bodyParts.map((d, i) => (
        <path
          key={`body-${i}`}
          d={d}
          fill={THEME.bodyFill}
          stroke={THEME.bodyStroke}
          strokeWidth="0.8"
          strokeLinejoin="round"
        />
      ))}

      {/* Muscle groups — line art default, colored fill when active */}
      {Object.entries(muscles).map(([muscleKey, paths]) => {
        const style = getMuscleStyle(muscleKey);
        const isHov = hoveredMuscle === muscleKey;
        const active = isActive(muscleKey);

        return paths.map((d, idx) => (
          <path
            key={`${muscleKey}-${idx}`}
            d={d}
            fill={style.fill}
            stroke={style.stroke}
            strokeWidth={style.strokeWidth}
            strokeOpacity={style.strokeOpacity}
            strokeLinejoin="round"
            style={{
              cursor: interactive ? 'pointer' : 'default',
              transition: 'fill 0.25s ease, stroke 0.2s ease, stroke-width 0.2s ease',
              filter: isHov && active ? `url(#glow-${viewLabel})` : 'none',
            }}
            onMouseEnter={interactive ? () => setHoveredMuscle(muscleKey) : undefined}
            onMouseLeave={interactive ? () => setHoveredMuscle(null) : undefined}
            onClick={interactive ? (e) => { e.stopPropagation(); setHoveredMuscle(isHov ? null : muscleKey); } : undefined}
            data-testid={`muscle-${viewLabel}-${muscleKey}`}
          />
        ));
      })}

      {/* Anatomical detail lines */}
      {showDetails && details.map((detail, i) => {
        const active = isActive(detail.m);
        const style = getMuscleStyle(detail.m);
        return (
          <path
            key={`detail-${i}`}
            d={detail.d}
            fill="none"
            stroke={active ? style.stroke : THEME.detailStroke}
            strokeWidth="0.4"
            strokeOpacity={active ? 0.5 : 0.35}
            pointerEvents="none"
          />
        );
      })}

      {/* View label */}
      <text
        x="100" y="437"
        textAnchor="middle"
        fill={THEME.labelColor}
        fontSize="7"
        fontFamily="monospace"
        letterSpacing="2"
      >
        {viewLabel.toUpperCase()}
      </text>

      {/* Optional overlay layer rendered inside the same SVG coord space
          (200×440). Used by MobilitySilhouette to draw joint dots on top
          of the muscle tints. */}
      {overlay}
    </svg>
  );
};

// ── Main Component ─────────────────────────────────────────────
const BodyMapSVG = ({
  muscleData = {},
  highlightMode = 'recovery',
  primaryMuscles = [],
  secondaryMuscles = [],
  size = 'md',
  showLabels = true,
  interactive = true,
  showBack = true,
  onMuscleClick,
  /**
   * Optional callback fired when a muscle is hovered (or null on leave).
   * Lets parents (e.g. MobilitySilhouette) render their own tooltip layer.
   */
  onMuscleHover,
  /**
   * Optional renderer that REPLACES the built-in tooltip body. Receives
   * (muscleKey, muscleDataEntry) → JSX. When provided, the recovery/exercise
   * default content is skipped. Use this to surface mobility-specific
   * tooltips (per-side angles, asymmetry deltas, etc.).
   */
  tooltipFormatter,
  /**
   * Optional overlay JSX rendered INSIDE the front/back SVG coord space
   * (200×440 viewBox). Used to draw joint markers or annotations on top
   * of the muscle layer without breaking layout.
   */
  frontOverlay,
  backOverlay,
  className = '',
}) => {
  const [hoveredMuscle, _setHoveredMuscle] = useState(null);
  const setHoveredMuscle = (m) => {
    _setHoveredMuscle(m);
    if (onMuscleHover) onMuscleHover(m);
  };
  const hoveredData = hoveredMuscle ? muscleData[hoveredMuscle] : null;

  const sizeClass = {
    sm: 'max-w-full',
    md: 'max-w-[520px]',
    lg: 'max-w-[720px]',
  }[size];

  const showDetails = size !== 'sm';

  return (
    <div className={`relative h-full ${className}`} data-testid="body-map-svg">
      {/* Front + Back side by side */}
      <div className={`mx-auto ${sizeClass} flex gap-1 h-full`}>
        <div className="flex-1 h-full" style={{ aspectRatio: showBack ? undefined : '200/440' }}>
          <BodyView
            bodyParts={FRONT_BODY}
            muscles={FRONT_MUSCLES}
            details={FRONT_DETAILS}
            muscleData={muscleData}
            highlightMode={highlightMode}
            primaryMuscles={primaryMuscles}
            secondaryMuscles={secondaryMuscles}
            hoveredMuscle={hoveredMuscle}
            setHoveredMuscle={interactive ? setHoveredMuscle : () => {}}
            interactive={interactive}
            viewLabel="front"
            showDetails={showDetails}
            overlay={frontOverlay}
          />
        </div>
        {showBack && (
          <div className="flex-1 h-full">
            <BodyView
              bodyParts={FRONT_BODY}
              muscles={BACK_MUSCLES}
              details={BACK_DETAILS}
              muscleData={muscleData}
              highlightMode={highlightMode}
              primaryMuscles={primaryMuscles}
              secondaryMuscles={secondaryMuscles}
              hoveredMuscle={hoveredMuscle}
              setHoveredMuscle={interactive ? setHoveredMuscle : () => {}}
              interactive={interactive}
              viewLabel="back"
              showDetails={showDetails}
              overlay={backOverlay}
            />
          </div>
        )}
      </div>

      {/* Hover Tooltip */}
      {interactive && hoveredMuscle && (
        <div
          className="absolute top-2 right-2 bg-zinc-900/95 border border-zinc-700 rounded-sm px-3 py-2 pointer-events-none z-10 backdrop-blur-sm min-w-[180px]"
          data-testid="body-map-tooltip"
        >
          <p className="font-secondary font-bold text-white uppercase text-xs tracking-wider">
            {formatMuscle(hoveredMuscle)}
          </p>
          {tooltipFormatter ? (
            tooltipFormatter(hoveredMuscle, hoveredData)
          ) : (
            <>
              {highlightMode === 'recovery' && hoveredData && (
                <>
                  <p
                    style={{ color: hoveredData.color }}
                    className="font-bold text-xl font-secondary"
                  >
                    {hoveredData.recovery_pct}%
                  </p>
                  <p className="text-zinc-500 text-xs">recovered</p>
                  {hoveredData.source_exercises?.length > 0 && (
                    <p className="text-zinc-400 text-xs mt-1">
                      {hoveredData.source_exercises.join(', ')}
                    </p>
                  )}
                  {hoveredData.hours_since_trained != null && (
                    <p className="text-zinc-500 text-xs">
                      {hoveredData.hours_since_trained < 24
                        ? `${Math.round(hoveredData.hours_since_trained)}h ago`
                        : `${Math.round(hoveredData.hours_since_trained / 24)}d ago`}
                    </p>
                  )}
                </>
              )}
              {highlightMode === 'exercise' && (
                <p
                  className="text-xs mt-0.5"
                  style={{
                    color: primaryMuscles?.includes(hoveredMuscle)
                      ? THEME.primary
                      : secondaryMuscles?.includes(hoveredMuscle)
                      ? THEME.secondary
                      : '#71717A',
                  }}
                >
                  {primaryMuscles?.includes(hoveredMuscle)
                    ? 'Primary'
                    : secondaryMuscles?.includes(hoveredMuscle)
                    ? 'Secondary'
                    : 'Not targeted'}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Exercise Legend */}
      {showLabels &&
        highlightMode === 'exercise' &&
        (primaryMuscles.length > 0 || secondaryMuscles.length > 0) && (
          <div className="mt-3 flex gap-6 justify-center text-xs">
            {primaryMuscles.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: THEME.primary }}
                  />
                  <span className="text-zinc-400 uppercase font-secondary font-bold">
                    Primary
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {primaryMuscles.map((m) => (
                    <span
                      key={m}
                      className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-sm"
                    >
                      {formatMuscle(m)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {secondaryMuscles.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 mb-1">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: THEME.secondary }}
                  />
                  <span className="text-zinc-400 uppercase font-secondary font-bold">
                    Secondary
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {secondaryMuscles.map((m) => (
                    <span
                      key={m}
                      className="px-2 py-0.5 bg-zinc-800 text-zinc-300 rounded-sm"
                    >
                      {formatMuscle(m)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

      {/* Recovery gradient legend */}
      {showLabels && highlightMode === 'recovery' && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <span className="text-xs text-zinc-500">Fatigued</span>
          <div
            className="w-32 h-2 rounded-full"
            style={{
              background:
                'linear-gradient(to right, #EF4444, #FB923C, #FDE047, #4ADE80, #22C55E)',
            }}
          />
          <span className="text-xs text-zinc-500">Recovered</span>
        </div>
      )}
    </div>
  );
};

export default BodyMapSVG;
